import { FC, useMemo, useState } from 'react';

import { DownloadIcon } from '@heroicons/react/outline';
import { GiftCouponSettlementSummaryItem } from '@shared/api/couponSettlement/couponSettlement.interface';
import { GiftRebateSettlement } from '@shared/api/giftRebateSettlement/giftRebateSettlement.interface';
import { GiftSettlementStatus } from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';
import { Loading } from '@shared/components/Loading';

import {
  amountColorClass,
  formatSettlementAmount,
  ROLE,
} from '../../utils/coupon-settlement.utils';
import { statusLabel } from '../../utils/rebate-settlement.utils';
import {
  buildUnifiedMonthlyRows,
  computeUnifiedTotals,
  unifiedYearOptions,
} from '../../utils/unified-settlement.utils';
import { exportUnifiedSettlementExcel } from '../../utils/unifiedSettlementExcel';
import {
  UnifiedDetailModal,
  UnifiedDetailTarget,
} from '../giftSettlement/UnifiedDetailModal';
import { PayoutDateSection } from '../giftSettlement/PayoutDateSection';
import { AmountCell, DetailButtons } from '../giftSettlement/UnifiedRowCells';

// ─────────────────────────────────────────────
// UnifiedMonthlySection
//
// 배송형·쿠폰형 정산을 한 표에서 월별 합계로 보여준다.
// 정산 문서 자체는 백엔드에서 종류별로 분리돼 있고 그대로 두되, 상조관리자가
// "이번 달에 내가 얼마인지"를 한 화면에서 보게 (연,월) 기준으로 금액만 합친다.
// 상세(라인 단위)는 각 종류 탭에서 계속 확인한다.
// ─────────────────────────────────────────────

export interface UnifiedMonthlySectionProps {
  /** 배송형 정산 문서 (배송형 탭과 동일한 데이터 — 금액 기준도 동일) */
  rebateItems: GiftRebateSettlement[] | undefined;
  /** 쿠폰형 정산서 요약 */
  couponItems: GiftCouponSettlementSummaryItem[] | undefined;
  /** 정산 문서 로딩 중 */
  loading: boolean;
  /** 미정산(아직 정산서로 안 묶인) 배송형 예상 수익금 */
  pendingDeliveryAmount?: number;
  /** 미정산 쿠폰형 순액 (발송 − 환불) */
  pendingCouponAmount?: number;
  /** 미정산 집계 로딩 중 */
  pendingLoading?: boolean;
  /** 배송형 탭으로 이동 */
  onOpenDelivery: () => void;
  /** 쿠폰형 탭으로 이동 */
  onOpenCoupon: () => void;
}

/** 요약 카드 */
const SummaryCard: FC<{
  label: string;
  amount: number | undefined;
  hint?: string;
  tone?: 'amber' | 'green' | 'gray' | 'indigo';
}> = ({ label, amount, hint, tone = 'gray' }) => {
  const toneClass =
    tone === 'amber'
      ? 'text-amber-700'
      : tone === 'green'
      ? 'text-green-700'
      : tone === 'indigo'
      ? 'text-indigo-700'
      : 'text-gray-800';
  return (
    <div className="rounded-lg bg-gray-50 p-4 text-center">
      <p className={`text-2xl font-bold tabular-nums ${toneClass}`}>
        {amount == null ? '-' : formatSettlementAmount(amount)}
      </p>
      <p className="mt-1 text-sm text-gray-500">{label}</p>
      {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    </div>
  );
};

export const UnifiedMonthlySection: FC<UnifiedMonthlySectionProps> = ({
  rebateItems,
  couponItems,
  loading,
  pendingDeliveryAmount,
  pendingCouponAmount,
  pendingLoading = false,
  onOpenDelivery,
  onOpenCoupon,
}) => {
  const allRows = useMemo(
    () => buildUnifiedMonthlyRows(rebateItems, couponItems),
    [rebateItems, couponItems],
  );

  const years = useMemo(() => unifiedYearOptions(allRows), [allRows]);
  const [year, setYear] = useState<number | 'all'>('all');

  const rows = useMemo(
    () => (year === 'all' ? allRows : allRows.filter((r) => r.year === year)),
    [allRows, year],
  );

  const totals = useMemo(() => computeUnifiedTotals(rows), [rows]);
  // 정산 예정(미지급)은 연도 필터와 무관하게 전체 기준 — "아직 못 받은 돈"이라 항상 전액이 궁금하다
  const allTotals = useMemo(() => computeUnifiedTotals(allRows), [allRows]);

  const draftDelivery = useMemo(
    () =>
      allRows.reduce(
        (sum, r) =>
          sum +
          (r.delivery?.status === GiftSettlementStatus.Draft ? r.delivery.amount : 0),
        0,
      ),
    [allRows],
  );
  const draftCoupon = allTotals.draftAmount - draftDelivery;

  const pendingTotal =
    pendingDeliveryAmount == null && pendingCouponAmount == null
      ? undefined
      : (pendingDeliveryAmount ?? 0) + (pendingCouponAmount ?? 0);

  const yearLabel = year === 'all' ? '전체' : `${year}년`;

  // 상세는 탭 이동 없이 이 자리에서 모달로 연다 (문서 id로 재조회 — UnifiedDetailModal)
  const [detailTarget, setDetailTarget] = useState<UnifiedDetailTarget | null>(null);
  const openDoc = (kind: 'delivery' | 'coupon', docId: string) =>
    setDetailTarget({
      kind,
      docId,
      // 쿠폰형은 목록 요약으로 헤더를 즉시 그린다 (본문만 스피너)
      couponSummary:
        kind === 'coupon' ? couponItems?.find((s) => s._id === docId) : undefined,
    });

  return (
    <div className="space-y-6">
      {/* 요약 카드 — 위에서부터 "받을 돈 → 쌓이는 중 → 받은 돈" 순 */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          label="정산 예정 (미지급)"
          amount={
            loading && !rebateItems && !couponItems ? undefined : allTotals.draftAmount
          }
          hint={`배송형 ${draftDelivery.toLocaleString(
            'ko-KR',
          )} · 쿠폰형 ${draftCoupon.toLocaleString('ko-KR')}`}
          tone="amber"
        />
        <SummaryCard
          label="미정산 (집계 전)"
          amount={pendingLoading && pendingTotal == null ? undefined : pendingTotal}
          hint={
            pendingTotal == null
              ? '집계 중'
              : `배송형 ${(pendingDeliveryAmount ?? 0).toLocaleString(
                  'ko-KR',
                )} · 쿠폰형 ${(pendingCouponAmount ?? 0).toLocaleString('ko-KR')}`
          }
        />
        <SummaryCard
          label={`${yearLabel} 지급 완료`}
          amount={totals.paidAmount}
          hint={`${totals.monthCount}개월`}
          tone="green"
        />
        <SummaryCard
          label={`${yearLabel} 총 정산금`}
          amount={totals.totalAmount}
          hint="예정 + 완료"
          tone="indigo"
        />
      </div>

      {/* 연도 필터 + 엑셀 */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setYear('all')}
            className={`rounded-full border px-3 py-1 text-sm ${
              year === 'all'
                ? 'border-indigo-600 bg-indigo-600 text-white'
                : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            전체
          </button>
          {years.map((y) => (
            <button
              key={y}
              onClick={() => setYear(y)}
              className={`rounded-full border px-3 py-1 text-sm ${
                year === y
                  ? 'border-indigo-600 bg-indigo-600 text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {y}년
            </button>
          ))}
        </div>
        <button
          onClick={() =>
            exportUnifiedSettlementExcel(
              rows,
              totals,
              `정산합계_${year === 'all' ? '전체' : `${year}년`}.xlsx`,
            )
          }
          disabled={rows.length === 0}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <DownloadIcon className="h-4 w-4" />
          엑셀 다운로드
        </button>
      </div>

      {/* 월별 합계 표 */}
      {loading && !rebateItems && !couponItems ? (
        <div className="center-box py-16">
          <Loading />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500">
              <tr>
                <th className="px-3 py-2.5 text-left">정산월</th>
                <th className="px-3 py-2.5 text-right">배송형 정산금</th>
                <th className="px-3 py-2.5 text-right">쿠폰형 정산금</th>
                <th className="px-3 py-2.5 text-right">합계</th>
                <th className="px-3 py-2.5 text-center">상세</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-10 text-center text-gray-400">
                    정산 내역이 없습니다.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.key} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-3 py-3 font-medium text-gray-800">
                    {r.year}년 {r.month}월
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right">
                    <AmountCell
                      side={r.delivery}
                      year={r.year}
                      month={r.month}
                      statusLabel={statusLabel}
                    />
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right">
                    <AmountCell
                      side={r.coupon}
                      year={r.year}
                      month={r.month}
                      statusLabel={statusLabel}
                    />
                  </td>
                  <td
                    className={`whitespace-nowrap px-3 py-3 text-right text-base font-bold tabular-nums ${amountColorClass(
                      r.totalAmount,
                    )}`}
                  >
                    {formatSettlementAmount(r.totalAmount)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-center">
                    <DetailButtons
                      delivery={r.delivery}
                      coupon={r.coupon}
                      onOpenDoc={openDoc}
                      onOpenTab={(kind) =>
                        kind === 'delivery' ? onOpenDelivery() : onOpenCoupon()
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-gray-50 font-semibold text-gray-800">
                <tr>
                  <td className="px-3 py-3">합계 ({totals.monthCount}개월)</td>
                  <td
                    className={`whitespace-nowrap px-3 py-3 text-right tabular-nums ${amountColorClass(
                      totals.deliveryAmount,
                    )}`}
                  >
                    {formatSettlementAmount(totals.deliveryAmount)}
                  </td>
                  <td
                    className={`whitespace-nowrap px-3 py-3 text-right tabular-nums ${amountColorClass(
                      totals.couponAmount,
                    )}`}
                  >
                    {formatSettlementAmount(totals.couponAmount)}
                  </td>
                  <td
                    className={`whitespace-nowrap px-3 py-3 text-right text-base tabular-nums ${amountColorClass(
                      totals.totalAmount,
                    )}`}
                  >
                    {formatSettlementAmount(totals.totalAmount)}
                  </td>
                  <td className="px-3 py-3 text-right text-xs text-gray-500">
                    지급완료 {formatSettlementAmount(totals.paidAmount)} · 정산예정{' '}
                    {formatSettlementAmount(totals.draftAmount)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* 지급일 축 — "그 날 총 얼마" (월별 표와 같은 필터 기준) */}
      <PayoutDateSection rows={rows} />

      <p className="text-xs text-gray-400">
        · 정산금은 각 탭(배송형·쿠폰형)의 금액을 정산월 기준으로 합산한 값입니다. 건별
        상세는 <b>상세</b> 버튼으로 이 화면에서 바로 볼 수 있습니다.
        <br />· &lsquo;미정산(집계 전)&rsquo;은 아직 정산서로 묶이지 않은 배송완료·쿠폰
        발송분의 예상 금액으로, 확정 금액이 아닙니다.
      </p>

      {/* 정산서 상세 — 탭 이동 없이 이 자리에서 (읽기 전용) */}
      <UnifiedDetailModal
        target={detailTarget}
        role={ROLE}
        onClose={() => setDetailTarget(null)}
      />
    </div>
  );
};
